import { useState, useCallback } from 'react'
import { X, Plus, Pencil, Trash2, Briefcase } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Badge } from '@/components/ui/badge'
import { Loading } from '@/components/ui/loading'
import { useWorkHistory, useCreateWorkHistory, useUpdateWorkHistory, useDeleteWorkHistory } from '@/hooks'
import { Employee, WorkHistory, WorkHistoryCreate, WorkHistoryUpdate } from '@/types'
import { toast } from 'sonner'

interface WorkHistoryPanelProps {
  employee: Employee
  onClose: () => void
}

interface WorkHistoryFormData {
  company_name: string
  position: string
  start_date: string
  end_date: string
  description: string
}

export function WorkHistoryPanel({ employee, onClose }: WorkHistoryPanelProps) {
  const [isFormOpen, setIsFormOpen] = useState(false)
  const [editingHistory, setEditingHistory] = useState<WorkHistory | null>(null)
  const [formData, setFormData] = useState<WorkHistoryFormData>({
    company_name: '',
    position: '',
    start_date: '',
    end_date: '',
    description: '',
  })

  const { data: workHistory, isLoading } = useWorkHistory(employee.id)
  const createWorkHistory = useCreateWorkHistory()
  const updateWorkHistory = useUpdateWorkHistory()
  const deleteWorkHistory = useDeleteWorkHistory()

  const isSubmitting = createWorkHistory.isPending || updateWorkHistory.isPending

  const handleOpenForm = useCallback((history?: WorkHistory) => {
    if (history) {
      setEditingHistory(history)
      setFormData({
        company_name: history.company_name,
        position: history.position || '',
        start_date: history.start_date || '',
        end_date: history.end_date || '',
        description: history.description || '',
      })
    } else {
      setEditingHistory(null)
      setFormData({
        company_name: '',
        position: '',
        start_date: '',
        end_date: '',
        description: '',
      })
    }
    setIsFormOpen(true)
  }, [])

  const handleCloseForm = useCallback(() => {
    setIsFormOpen(false)
    setEditingHistory(null)
  }, [])

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault()

    if (!formData.company_name) {
      toast.error('请填写公司名称')
      return
    }

    try {
      const data = {
        company_name: formData.company_name,
        position: formData.position || null,
        start_date: formData.start_date || null,
        end_date: formData.end_date || null,
        description: formData.description || null,
      }

      if (editingHistory) {
        await updateWorkHistory.mutateAsync({
          employeeId: employee.id,
          historyId: editingHistory.id,
          data,
        })
        toast.success('工作经历已更新')
      } else {
        await createWorkHistory.mutateAsync({
          employeeId: employee.id,
          data,
        })
        toast.success('工作经历已添加')
      }
      handleCloseForm()
    } catch (error) {
      toast.error(error instanceof Error ? error.message : '操作失败')
    }
  }, [formData, editingHistory, employee.id, createWorkHistory, updateWorkHistory, handleCloseForm])

  const handleDelete = useCallback(async (history: WorkHistory) => {
    if (!confirm('确定要删除这条工作经历吗？')) return
    try {
      await deleteWorkHistory.mutateAsync({
        employeeId: employee.id,
        historyId: history.id,
      })
      toast.success('工作经历已删除')
    } catch (error) {
      toast.error(error instanceof Error ? error.message : '删除失败')
    }
  }, [employee.id, deleteWorkHistory])

  const handleChange = (field: keyof WorkHistoryFormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <Card className="w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4 border-b border-slate-200 dark:border-slate-800">
          <div>
            <CardTitle className="text-lg flex items-center gap-2">
              <Briefcase className="w-5 h-5 text-green-500" />
              工作经历 - {employee.name}
            </CardTitle>
            <p className="text-sm text-slate-500 mt-1">工号：{employee.id}</p>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <X className="w-5 h-5" />
          </Button>
        </CardHeader>

        <CardContent className="flex-1 overflow-y-auto p-6">
          {isLoading ? (
            <Loading className="py-8" />
          ) : (
            <>
              {/* Work History List */}
              {workHistory && workHistory.length > 0 ? (
                <div className="space-y-4">
                  {workHistory.map((history) => (
                    <div
                      key={history.id}
                      className="p-4 rounded-lg border border-slate-200 dark:border-slate-800 hover:border-slate-300 dark:hover:border-slate-700 transition-colors"
                    >
                      <div className="flex items-start justify-between">
                        <div className="space-y-1">
                          <p className="font-medium text-slate-900 dark:text-white">
                            {history.company_name}
                          </p>
                          {history.position && (
                            <p className="text-sm text-slate-500">{history.position}</p>
                          )}
                          {(history.start_date || history.end_date) && (
                            <p className="text-xs text-slate-400">
                              {history.start_date || '?'} ~ {history.end_date || '至今'}
                            </p>
                          )}
                          {history.description && (
                            <p className="text-sm text-slate-600 dark:text-slate-400 mt-2">
                              {history.description}
                            </p>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleOpenForm(history)}
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(history)}
                            className="text-rose-600 hover:text-rose-700"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-slate-500">
                  暂无工作经历记录
                </div>
              )}

              {/* Add Button */}
              {!isFormOpen && (
                <Button
                  variant="outline"
                  className="w-full mt-4"
                  onClick={() => handleOpenForm()}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  添加工作经历
                </Button>
              )}

              {/* Work History Form */}
              {isFormOpen && (
                <form onSubmit={handleSubmit} className="mt-6 p-4 rounded-lg border border-slate-200 dark:border-slate-800 space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-slate-700 dark:text-slate-300">
                        公司名称 <span className="text-rose-500">*</span>
                      </label>
                      <Input
                        value={formData.company_name}
                        onChange={(e) => handleChange('company_name', e.target.value)}
                        placeholder="请输入公司名称"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-slate-700 dark:text-slate-300">职位</label>
                      <Input
                        value={formData.position}
                        onChange={(e) => handleChange('position', e.target.value)}
                        placeholder="请输入职位"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-slate-700 dark:text-slate-300">开始日期</label>
                      <Input
                        type="month"
                        value={formData.start_date}
                        onChange={(e) => handleChange('start_date', e.target.value)}
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-slate-700 dark:text-slate-300">结束日期</label>
                      <Input
                        type="month"
                        value={formData.end_date}
                        onChange={(e) => handleChange('end_date', e.target.value)}
                        placeholder="留空表示至今"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700 dark:text-slate-300">工作描述</label>
                    <Textarea
                      value={formData.description}
                      onChange={(e) => handleChange('description', e.target.value)}
                      placeholder="请输入工作描述..."
                      rows={3}
                    />
                  </div>

                  <div className="flex justify-end gap-3">
                    <Button type="button" variant="outline" onClick={handleCloseForm}>
                      取消
                    </Button>
                    <Button type="submit" disabled={isSubmitting}>
                      {isSubmitting ? '提交中...' : editingHistory ? '保存' : '添加'}
                    </Button>
                  </div>
                </form>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
